import type { Client, Response } from "~/types";

export type OtpVerificationCodeCredentials = {
  country_id: number;
  mobile: number | string;
  verification_code: number | string;
  business_identifier?: string | null;
};

export type CompleteProfileCredentials = {
  name: string;
  email?: string;
  gender?: string;
}

export type SignInCredentials = {
  mobile: string;
  country_id: number | string;
}

export type VerificationTokenCredentials = {
  mobile: string;
  country_id: string;
  verification_token: string;
}

// Value is initialized in: ~/plugins/auth.ts
export const useClient = <T = Client>() => {
  return useState<T | undefined | null>("client", () => undefined);
};

export const useAuth = <T = Client>() => {
  const { apiVersion } = useRuntimeConfig().public;
  const businessStore = useBusinessStore();

  const client = useClient<T>();
  const isLoggedIn = computed(() => !!client.value);

  async function refresh() {
    try {
      const response = await fetchCurrentClient<T>();
      if (response) {
        client.value = response.data;
      }
    } catch {
      client.value = null;
    }
  }

  async function signIn(credentials: SignInCredentials) {
    if (isLoggedIn.value) return;
    return await $larafetch(`/sign-in`, { method: "post", body: credentials });
  }

  async function checkOtpVerificationCode(credentials: OtpVerificationCodeCredentials) {
    return await $larafetch(`/check-otp-verification-code`, { method: "post", body: credentials });
  }

  async function checkVerificationToken(credentials: VerificationTokenCredentials) {
    return await $larafetch(`/check-verification-token`, { method: "post", body: credentials });
  }

  async function resendOtpVerificationCode(credentials: SignInCredentials) {
    return await $larafetch(`/resend-otp-verification-code`, { method: "post", body: credentials });
  }

  async function completeProfile(credentials: CompleteProfileCredentials) {
    return await $larafetch(`/api/${apiVersion}/account/complete`, { method: "post", body: credentials });
  }

  async function logout() {
    if (!isLoggedIn.value) return;
    await $larafetch(`/logout`);
    client.value = null;
    useClearCookies();
    businessStore.clearBooking();
  }

  // check this ????????????
  // $larafetch<{ status: string }>

  return {
    client,
    isLoggedIn,
    signIn,
    checkOtpVerificationCode,
    checkVerificationToken,
    resendOtpVerificationCode,
    completeProfile,
    logout,
    refresh,
  };
};

export const fetchCurrentClient = async <T = Client>() => {
  try {
    const { data, error } = await useLarafetch<{ data: T }>(`/api/client`);
    return data.value as Response<T>;
  } catch (error: any) {
    if ([401, 419].includes(error?.response?.status)) return null;
    throw error;
  }
};
